export interface ServiceData {
  slug: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  heroSubhead: string;
  heroCopy: string;
  benefits: string[];
  materials: { title: string; items: string[] }[];
  layouts: string[];
  useCases?: string[];
  faqs: { question: string; answer: string }[];
  galleryItems: { label: string; imageQuery: string }[];
}

export const services: Record<string, ServiceData> = {
  'shower-doors': {
    slug: 'shower-doors',
    metaTitle: 'Custom Frameless Shower Doors in NYC | Same-Week Install | NY Glass',
    metaDescription: 'Design & install custom frameless shower doors in NYC. Precise measurement, premium hardware, fast fabrication, pro installation. Get your instant quote.',
    h1: 'Custom Frameless Shower Doors in NYC',
    heroSubhead: 'Sleek, water-tight, made-to-measure. From design to flawless install.',
    heroCopy: 'Turn steam into shine. Seamless, water-tight, custom-cut for your space.',
    benefits: [
      'Space-expanding look with easy-clean coatings',
      'Durable tempered glass with thickness options',
      'Hardware finishes to match your bathroom',
      'Same-week installation in all NYC boroughs',
    ],
    materials: [
      {
        title: 'Glass Options',
        items: ['3/8" tempered', '1/2" tempered', 'Clear glass', 'Low-iron glass', 'Frosted/textured'],
      },
      {
        title: 'Hardware Finishes',
        items: ['Chrome', 'Brushed nickel', 'Matte black', 'Brass', 'Oil-rubbed bronze'],
      },
      {
        title: 'Features',
        items: ['Protective coating add-on', 'Seals & sweeps', 'Wall-mount hinges', 'Pivot hinges'],
      },
    ],
    layouts: [
      'Single door',
      'Door + panel',
      'Neo-angle',
      'Inline',
      'Tub slider',
      'Steam enclosure',
    ],
    galleryItems: [
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
    ],
    faqs: [
      {
        question: 'How long from measure to install?',
        answer: 'Typically 5-7 business days. We measure precisely at your location, fabricate your custom enclosure, and schedule installation at your convenience. Rush service available for most projects.',
      },
      {
        question: 'What\'s the difference between low-iron and standard clear glass?',
        answer: 'Low-iron glass has minimal green tint, providing ultra-clear transparency that\'s ideal for white tile and bright bathrooms. Standard clear has a subtle green edge visible on thicker glass.',
      },
      {
        question: 'Do you offer steam enclosures?',
        answer: 'Yes. Steam enclosures require a transom (top panel) and we use appropriate seals and hardware rated for steam environments. We can integrate with your steam generator installation.',
      },
      {
        question: 'Can you notch around benches and angles?',
        answer: 'Absolutely. We template all irregularities including benches, niches, sloped ceilings, and out-of-plumb walls to ensure a perfect fit.',
      },
      {
        question: 'How do I care for and clean my shower door?',
        answer: 'Squeegee after each use. Weekly cleaning with non-abrasive glass cleaner. If you choose our protective coating, water beads off naturally reducing mineral buildup.',
      },
      {
        question: 'What hardware finishes are available?',
        answer: 'We offer chrome, brushed nickel, matte black, polished brass, and oil-rubbed bronze. All hardware is solid brass or stainless steel construction.',
      },
      {
        question: 'Do you service all NYC boroughs?',
        answer: 'Yes. We provide measurement, fabrication, and installation services throughout Manhattan, Brooklyn, Queens, Bronx, and Staten Island.',
      },
      {
        question: 'Is the glass safe if it breaks?',
        answer: 'All shower glass is tempered safety glass that crumbles into small pebbles rather than sharp shards if broken. This exceeds building code requirements.',
      },
    ],
  },
  'mirrors': {
    slug: 'mirrors',
    metaTitle: 'Custom Mirrors NYC for Gyms, Studios & Homes | Large Wall Mirrors',
    metaDescription: 'Large custom mirrors for gyms, dance & home. Seamless wall spans, safety backing, precise alignment, polished edges. Fast NYC installation.',
    h1: 'Custom Mirrors for Gyms, Studios & Homes',
    heroSubhead: 'Edge-to-edge reflections, laser-straight seams.',
    heroCopy: 'Wall-to-wall precision for studios, gyms and homes.',
    benefits: [
      'Safety-backed mirror glass for large spans',
      'Seam matching for studios with outlets & cutouts handled',
      'Gym sleeves and rail guards optional',
      'Beveled or polished edges for elegant finish',
    ],
    materials: [
      {
        title: 'Mirror Types',
        items: ['Standard mirror', 'Low-iron mirror', 'Gray tint', 'Bronze tint', 'Antique mirror'],
      },
      {
        title: 'Edge Options',
        items: ['Polished edge', 'Beveled edge', 'Seamed edge', 'No edge (covered)'],
      },
      {
        title: 'Mounting Systems',
        items: ['J-channel mounting', 'Standoff mounts', 'Adhesive + clips', 'Mirror walls with barres'],
      },
    ],
    useCases: [
      'Fitness centers & gyms',
      'Dance & yoga studios',
      'Home gyms',
      'Bathrooms with anti-fog',
      'Foyers & hallways',
      'Retail dressing rooms',
    ],
    layouts: [],
    galleryItems: [
      { label: '', imageQuery: '/images/studio-mirrors/studio-mirrors_1.webp' },
      { label: '', imageQuery: '/images/studio-mirrors/studio-mirrors_2.webp' },
      { label: '', imageQuery: '/images/studio-mirrors/studio-mirrors_3.webp' },
      { label: '', imageQuery: '/images/studio-mirrors/studio-mirrors_4.webp' },
      { label: '', imageQuery: '/images/studio-mirrors/studio-mirrors_5.webp' },
      { label: '', imageQuery: '/images/studio-mirrors/studio-mirrors_6.webp' },
      { label: '', imageQuery: '/images/studio-mirrors/studio-mirrors_7.webp' },
      { label: '', imageQuery: '/images/studio-mirrors/studio-mirrors_8.webp' },
      { label: '', imageQuery: '/images/studio-mirrors/studio-mirrors_9.webp' },
    ],
    faqs: [
      {
        question: 'What is the maximum panel size?',
        answer: 'Single mirror panels up to 8\' × 12\' depending on installation access. For larger walls we create seamless multi-panel installations with precision-matched seams.',
      },
      {
        question: 'How do you handle building elevators and tight spaces?',
        answer: 'We measure access paths during the initial visit. Large mirrors can be carried through standard doorways and elevators with proper technique and protective padding.',
      },
      {
        question: 'Where do you place seams on multi-panel walls?',
        answer: 'Seams are strategically placed at natural break points (e.g., 6\' intervals) and aligned vertically for minimal visibility. We discuss seam placement during measurement.',
      },
      {
        question: 'What backing types are available?',
        answer: 'Safety backing (film that holds glass together if broken) is standard for gyms and studios. Vinyl backing provides additional moisture protection for bathroom applications.',
      },
      {
        question: 'What mounting systems do you use?',
        answer: 'J-channel (top/bottom clips), adhesive with rosette clips, standoff mounts, or custom frames depending on wall type and aesthetic preference.',
      },
      {
        question: 'Can you cut around outlets and switches?',
        answer: 'Yes, we template and cut precise openings for all electrical fixtures, ensuring mirrors sit flush to the wall.',
      },
      {
        question: 'Do you install ballet barres?',
        answer: 'We partner with professional barre installers and coordinate installation for dance studios requiring wall-mounted barres over mirrors.',
      },
      {
        question: 'How do I clean large gym mirrors?',
        answer: 'Use ammonia-free glass cleaner or a vinegar-water solution with microfiber cloths. Avoid abrasive materials that can scratch the surface.',
      },
    ],
  },
  'glass-railings': {
    slug: 'glass-railings',
    metaTitle: 'Interior & Exterior Glass Railings NYC | Elegant, Safe, Custom',
    metaDescription: 'Glass railings & guards for stairs, balconies, decks. Elegant hardware, laminated/tempered options, precise install. Book a measurement.',
    h1: 'Elegant Glass Railings for Stairs & Balconies',
    heroSubhead: 'Unobstructed views with laminated safety packages.',
    heroCopy: 'Views, not posts. Safe, elegant glass barriers for stairs and balconies.',
    benefits: [
      'Unobstructed views with laminated safety packages',
      'Post-free shoe systems or standoff mounts',
      'Handrail options in wood or metal with top cap or capless',
      'Interior and exterior rated hardware',
    ],
    materials: [
      {
        title: 'Glass Packages',
        items: ['1/2" laminated', '3/4" laminated', 'Tempered options', 'Clear or tinted', 'Low-iron'],
      },
      {
        title: 'Mounting Systems',
        items: ['Base-shoe channel', 'Standoff mounts', 'Post & clamp', 'Fascia mount'],
      },
      {
        title: 'Handrails',
        items: ['Wood top rail', 'Stainless steel', 'Aluminum cap', 'Capless option', 'Wall-mounted'],
      },
    ],
    useCases: [
      'Residential interior stairs',
      'Mezzanines & lofts',
      'Roof decks (where permitted)',
      'Balconies & terraces',
      'Lobby barriers',
      'Commercial atriums',
    ],
    layouts: [],
    galleryItems: [
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
    ],
    faqs: [
      {
        question: 'What\'s the difference between interior and exterior hardware?',
        answer: 'Exterior hardware uses marine-grade stainless steel and specialized gaskets to withstand weather, salt air, and temperature changes. Interior hardware can use a wider finish palette.',
      },
      {
        question: 'How do I clean glass railings?',
        answer: 'Standard glass cleaner for interior; exterior railings benefit from monthly cleaning to remove environmental buildup. Stainless hardware can be polished with specialized cleaners.',
      },
      {
        question: 'Are glass railings safe with children and pets?',
        answer: 'Yes. We use laminated glass (two layers bonded with an interlayer) that stays intact even if cracked. Railing height and spacing meet or exceed code requirements.',
      },
      {
        question: 'What are typical lead times?',
        answer: 'Custom glass railings typically require 3-4 weeks from measurement to installation, accounting for fabrication and scheduling.',
      },
      {
        question: 'Can glass railings be installed on existing stairs?',
        answer: 'Usually yes, though it depends on the structural adequacy of the existing stair and available mounting surfaces. We assess during measurement.',
      },
      {
        question: 'Do you offer colored or tinted glass?',
        answer: 'Yes. We offer gray, bronze, and other tints. Low-iron (ultra-clear) is popular for unobstructed views.',
      },
      {
        question: 'What thickness glass is required?',
        answer: 'Code-compliant railings typically use 1/2" laminated glass minimum. Taller railings or high-traffic areas may require 3/4" laminated.',
      },
      {
        question: 'Do you handle permits and code compliance?',
        answer: 'We provide shop drawings and specifications for permit applications. Installation meets NYC building codes and is performed by licensed professionals.',
      },
    ],
  },
  'glass-tabletops-shelves': {
    slug: 'glass-tabletops-shelves',
    metaTitle: 'Custom Glass Table Tops & Shelves NYC | Protectors & Replacements',
    metaDescription: 'Custom glass table tops & shelves cut to size: clear/low-iron, beveled/polished edges, tempered/laminated options. Fast NYC delivery & install.',
    h1: 'Custom Glass Table Tops & Shelves',
    heroSubhead: 'Protects wood finishes with scratch-resistant edges.',
    heroCopy: 'Protect and refresh with crystal-clear, custom-cut glass.',
    benefits: [
      'Protects wood finishes with scratch-resistant edges',
      'Precisely templated for antiques & irregular shapes',
      'Non-slip pads and clear bumpers included',
      'Beveled, OGEE, and pencil edge profiles available',
    ],
    materials: [
      {
        title: 'Thickness Options',
        items: ['1/4" annealed', '3/8" annealed', '1/2" annealed', '1/2" tempered', '3/4" tempered'],
      },
      {
        title: 'Edge Profiles',
        items: ['Flat polished', 'Beveled', 'Pencil edge', 'OGEE', 'Waterfall'],
      },
      {
        title: 'Glass Types',
        items: ['Clear standard', 'Low-iron (ultra-clear)', 'Bronze tint', 'Gray tint', 'Ribbed/reeded pattern'],
      },
    ],
    useCases: [
      'Dining table protectors',
      'Coffee & console tables',
      'Desk surfaces',
      'Floating shelves',
      'Cabinet inserts',
      'Antique furniture protection',
    ],
    layouts: [],
    galleryItems: [
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
    ],
    faqs: [
      {
        question: 'Should I use tempered or annealed glass for tabletops?',
        answer: 'Tempered is stronger and safer (breaks into small pebbles), ideal for heavy-use dining tables. Annealed can be cut after fabrication and is suitable for protective overlays on existing furniture.',
      },
      {
        question: 'What are the safe overhang limits?',
        answer: 'Generally 1/3 of the support depth. For example, a 12" wide shelf can overhang 4" safely. We provide engineering guidance based on thickness and span.',
      },
      {
        question: 'Which edge profile should I choose?',
        answer: 'Flat polished is modern and economical. Beveled adds elegance. OGEE is traditional. Pencil edge is soft and contemporary. We provide samples during consultation.',
      },
      {
        question: 'How do I clean glass table tops and shelves?',
        answer: 'Use standard glass cleaner and microfiber cloths. Avoid abrasive scrubbers. For textured glass, follow the grain of the pattern.',
      },
      {
        question: 'Can you template irregular or antique furniture?',
        answer: 'Yes. We create physical templates for complex shapes, cutouts, and curved edges to ensure perfect fit.',
      },
      {
        question: 'Do you provide non-slip pads?',
        answer: 'Yes. All table tops include clear silicone bumpers that prevent sliding while protecting the furniture surface beneath.',
      },
      {
        question: 'What\'s the lead time for custom glass tops?',
        answer: 'Standard shapes in stock thicknesses: 3-5 business days. Complex templates or specialty glass: 1-2 weeks.',
      },
      {
        question: 'Do you install the glass or just deliver?',
        answer: 'We offer both delivery-only and full installation services. Large or complex pieces include installation for safety and proper placement.',
      },
    ],
  },
  'storefronts': {
    slug: 'storefronts',
    metaTitle: 'Storefronts & Commercial Glass NYC | Fast Repairs & New Installs',
    metaDescription: 'Commercial storefront glass: repairs & new installs, doors, transoms, security laminates, emergency board-up. Serving all NYC boroughs.',
    h1: 'Storefronts & Commercial Glass',
    heroSubhead: 'Rapid response for damages with temporary board-ups.',
    heroCopy: 'First impressions in glass. Fast repairs and spotless new installs.',
    benefits: [
      'Rapid response for damages with temporary board-ups',
      'High-clarity panes with security and laminated options',
      'ADA door hardware and closers',
      'After-hours installation to minimize business disruption',
    ],
    materials: [
      {
        title: 'Framing Systems',
        items: ['Aluminum storefront', 'Steel frames', 'Frameless systems', 'Curtain wall'],
      },
      {
        title: 'Door Types',
        items: ['Pivot doors', 'Patch-fitting doors', 'Sliding doors', 'Automatic doors', 'Revolving doors'],
      },
      {
        title: 'Glass Options',
        items: ['Annealed clear', 'Tempered safety', 'Laminated security', 'Tinted/film', 'Low-E insulated'],
      },
    ],
    useCases: [
      'Retail storefronts',
      'Restaurants & cafes',
      'Office building entries',
      'Art galleries',
      'Medical facilities',
      'Hotel lobbies',
    ],
    layouts: [],
    galleryItems: [
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
      { label: '', imageQuery: '' },
    ],
    faqs: [
      {
        question: 'How quickly can you respond to emergency glass breakage?',
        answer: 'We offer same-day emergency board-up service throughout NYC. Permanent glass replacement is typically scheduled within 2-3 business days depending on glass type.',
      },
      {
        question: 'Do you handle permits and building coordination?',
        answer: 'Yes. We prepare permit applications, coordinate with building management, and ensure compliance with NYC building codes and landmark regulations where applicable.',
      },
      {
        question: 'Can you install after business hours?',
        answer: 'Yes. We offer evening and weekend installation to minimize disruption to your business operations. After-hours service may include a surcharge.',
      },
      {
        question: 'Do you offer logo frosting or window films?',
        answer: 'We work with specialized film installers for custom frosting, logos, and decorative films. We can coordinate the full installation as a turnkey service.',
      },
      {
        question: 'What maintenance is required for commercial glass doors?',
        answer: 'Door closers should be adjusted seasonally. Pivots and patch fittings need periodic inspection. We offer maintenance contracts for high-traffic installations.',
      },
      {
        question: 'What security glass options are available?',
        answer: 'Laminated glass (two layers with interlayer) provides break-resistance and holds together when impacted. Available in various thicknesses for different security levels.',
      },
      {
        question: 'Can you match existing storefront systems?',
        answer: 'Yes. We identify and source matching aluminum profiles, or provide compatible retrofit solutions for older systems.',
      },
      {
        question: 'Do you provide warranty on commercial installations?',
        answer: 'Yes. All installations include a 1-year labor warranty. Glass and hardware carry manufacturer warranties ranging from 1-10 years depending on product.',
      },
    ],
  },
};

export const serviceOrder = [
  'shower-doors',
  'mirrors',
  'glass-railings',
  'glass-tabletops-shelves',
  'storefronts',
];
